import { useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { errorText } from '../../../features/edo/errorText'
import { useApp } from '../../../i18n'
import type { SigningOperation } from '../../../lib/types'
import { tone } from '../../../theme/tones'
import { Button, Card } from '../../../ui'
import { SigningTicket } from './SigningTicket'
import { signingVM } from '../signingVM'

/**
 * Карточка операции подписания: что подписываем, чем это закончилось и талон, пока подпись
 * ждут. Сам талон (QR и кнопка «Открыть в приложении») — в `SigningTicket`: он же показывается
 * контрагенту на публичной странице.
 */
export function SigningOperationCard({
  op,
  ticket = true,
}: {
  op: SigningOperation
  /**
   * `false` — не показывать талон. Так выглядит операция, адресованная КОНТРАГЕНТУ: автор
   * заявки следит за её статусом, но отсканировать этот QR своим сертификатом не может —
   * SIMA сверяет адресата на телефоне и такую подпись отобьёт.
   */
  ticket?: boolean
}) {
  const { t } = useApp()
  const vm = signingVM(op)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  /** Файл приватный: тянем с токеном и отдаём браузеру ссылкой на blob (как вложения ЭДО). */
  const download = async () => {
    if (busy) return
    setBusy(true)
    setErr('')
    try {
      const blob = await api.signingDocument(op.id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = op.documentName ?? `${op.id}.pdf`
      link.click()
      // Отложенный revoke: сразу после click браузер мог ещё не начать чтение blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setErr(t(errorText(e)))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card gap={16} padding={16}>
      <XStack gap={12} alignItems="center" justifyContent="space-between">
        <Text fontSize={15} fontWeight="600" color="$color" numberOfLines={2} flex={1}>
          {op.documentName ?? t({ ru: 'Подтверждение входа', en: 'Login confirmation', az: 'Girişin təsdiqi' })}
        </Text>
        <Stack
          paddingHorizontal={10}
          paddingVertical={4}
          borderRadius={999}
          backgroundColor={tone(vm.tone).soft}
        >
          <Text fontSize={12} fontWeight="600" color={tone(vm.tone).text}>
            {t(vm.statusLabel)}
          </Text>
        </Stack>
      </XStack>

      {ticket && vm.canShowQr && vm.link ? <SigningTicket link={vm.link} /> : null}

      {op.status === 'completed' ? (
        <YStack gap={6}>
          <Text fontSize={13} color={tone(op.pdfCheck?.ok ? 'green' : 'slate').text}>
            {t(vm.signatures)}
          </Text>
          {vm.downloadUploadId ? (
            <Button disabled={busy} opacity={busy ? 0.5 : 1} onPress={download}>
              {t({ ru: 'Скачать документ', en: 'Download document', az: 'Sənədi yüklə' })}
            </Button>
          ) : null}
          {err ? (
            <Text fontSize={13} color={tone('red').text}>
              {err}
            </Text>
          ) : null}
          {op.signerSubject ? (
            <Text fontSize={13} color="$muted" numberOfLines={2}>
              {op.signerSubject.replace(/\n/g, ' · ')}
            </Text>
          ) : null}
        </YStack>
      ) : null}

      {op.error ? (
        <Text fontSize={13} color={tone('red').text}>
          {op.error}
        </Text>
      ) : null}
    </Card>
  )
}
